import type { WizardStepProps } from '../../../../types/builder.types'
import { getAggregatableFields, getAllFields } from '../../../../config/builder-schemas'
import { useDataSources } from '../../../../hooks/useDataSources'
import { useMemo } from 'react'
import {
  fieldsFromCustomQuery,
  getCustomQueryConfigSignature,
} from '../../../../utils/custom-query-builder-fields'

export function StackedBarConfiguration({ config, onChange, onNext, onPrev }: WizardStepProps) {
  const { dataSources } = useDataSources()

  const cqSig = useMemo(
    () => getCustomQueryConfigSignature(config.customQuery),
    [config.customQuery]
  )

  const { aggregatableFields, allFields } = useMemo(() => {
    if (!config.dataSource) return { aggregatableFields: [], allFields: [] }

    if (
      (config.dataSource === 'custom_query' ||
        (config.dataSource?.includes('ckan_data_generic') && config.customQuery)) &&
      config.customQuery?.config?.selectedFields?.length
    ) {
      const fieldsArray = fieldsFromCustomQuery(config.customQuery)
      return {
        aggregatableFields: fieldsArray,
        allFields: fieldsArray,
      }
    }

    // CASO 2: Fuente de datos normal (no custom_query)
    const dynamicSource = dataSources.find(ds => ds.tableName === config.dataSource)
    if (dynamicSource) {
      return {
        aggregatableFields: dynamicSource.fields.filter(f => f.aggregatable),
        allFields: dynamicSource.fields
      }
    }

    // CASO 3: Fallback a funciones estáticas
    return {
      aggregatableFields: getAggregatableFields(config.dataSource),
      allFields: getAllFields(config.dataSource)
    }
  }, [dataSources, config.dataSource, cqSig])

  const stackedBarConfig = config.stackedBarConfig || { 
    xAxisKey: '', 
    yAxisKeys: [], 
    layout: 'vertical' as 'vertical' | 'horizontal' 
  };
  const usePivot = Boolean(stackedBarConfig.stackCategoryKey && stackedBarConfig.valueKey);
  const isValid = Boolean(
    config.metadata?.title &&
    stackedBarConfig.xAxisKey &&
    (stackedBarConfig.yAxisKeys?.length > 0 || usePivot)
  )

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onPrev} className="mb-6 text-green-600 hover:underline">
        ← Volver
      </button>

      <h2 className="text-2xl font-bold mb-2">Configurar Gráfico de Barras Apiladas</h2>
      <p className="text-gray-600 mb-6">
        Compara múltiples series de datos apiladas en barras
      </p>

      <div className="space-y-6 bg-white border rounded-lg p-6">
        {/* Título */}
        <div>
          <label className="block font-medium mb-2">
            Título <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Ej: Especies por Provincia"
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={config.metadata?.title || ''}
            onChange={(e) =>
              onChange({
                ...config,
                metadata: { ...config.metadata, title: e.target.value },
              })
            }
          />
        </div>

        {/* Eje X (Categorías) */}
        <div>
          <label className="block font-medium mb-2">
            Eje X (Categorías) <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={stackedBarConfig.xAxisKey || ''}
            onChange={(e) =>
              onChange({
                ...config,
                stackedBarConfig: { ...stackedBarConfig, xAxisKey: e.target.value },
              })
            }
          >
            <option value="">Selecciona un campo...</option>
            {allFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Campo para las categorías en el eje X (ej: municipio, provincia, año)
          </p>
        </div>

        {/* Modo pivot: categoría de apilado + valor (ej: especie + volumen por municipio) */}
        <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
          <p className="text-sm font-medium text-gray-700">
            Opción: generar series desde un campo (ej. una barra por municipio, segmentos por especie)
          </p>
          <div>
            <label className="block text-sm mb-1">Campo que define cada serie apilada (ej. especie)</label>
            <select
              className="w-full border rounded px-3 py-2 text-sm"
              value={stackedBarConfig.stackCategoryKey || ''}
              onChange={(e) =>
                onChange({
                  ...config,
                  stackedBarConfig: { ...stackedBarConfig, stackCategoryKey: e.target.value || undefined },
                })
              }
            >
              <option value="">No usar (elegir series manualmente abajo)</option>
              {allFields.filter((f) => f.type === 'string').map((field) => (
                <option key={field.name} value={field.name}>{field.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm mb-1">Campo numérico (valor a sumar por categoría)</label>
            <select
              className="w-full border rounded px-3 py-2 text-sm"
              value={stackedBarConfig.valueKey || ''}
              onChange={(e) =>
                onChange({
                  ...config,
                  stackedBarConfig: { ...stackedBarConfig, valueKey: e.target.value || undefined },
                })
              }
            >
              <option value="">Selecciona...</option>
              {aggregatableFields.map((field) => (
                <option key={field.name} value={field.name}>{field.label}</option>
              ))}
            </select>
          </div>
          {usePivot && (
            <p className="text-xs text-green-700">
              Se generarán tantas series como valores distintos tenga el campo de serie (ej. una por especie).
            </p>
          )}
        </div>

        {/* Campos del Eje Y (Series múltiples; se ignoran si está activo el modo pivot) */}
        <div>
          <label className="block font-medium mb-2">
            Campos del Eje Y (mantén Ctrl para seleccionar varios){' '}
            <span className="text-red-500">*</span>
          </label>
          <select
            multiple
            size={5}
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={stackedBarConfig.yAxisKeys || []}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions).map(
                (opt) => opt.value
              )
              onChange({
                ...config,
                stackedBarConfig: { ...stackedBarConfig, yAxisKeys: selected },
              })
            }}
          >
            {aggregatableFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            {usePivot ? 'Ignorado cuando usas la opción de serie por campo arriba.' : `Seleccionados: ${stackedBarConfig.yAxisKeys?.join(', ') || 'ninguno'}`}
          </p>
        </div>

        {/* Orientación */}
        <div>
          <label className="block font-medium mb-2">Orientación</label>
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: 'vertical', label: 'Vertical', icon: '📊' },
              { value: 'horizontal', label: 'Horizontal', icon: '📈' },
            ].map((layout) => (
              <button
                key={layout.value}
                type="button"
                className={`
                  border-2 rounded p-4 text-center transition-all
                  ${
                    (stackedBarConfig.layout || 'vertical') === layout.value
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }
                `}
                onClick={() =>
                  onChange({
                    ...config,
                    stackedBarConfig: {
                      ...stackedBarConfig,
                      layout: layout.value as 'vertical' | 'horizontal',
                    },
                  })
                }
              >
                <div className="text-2xl mb-1">{layout.icon}</div>
                <div className="text-sm font-medium">{layout.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Opción de normalización */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="normalized"
            checked={stackedBarConfig.normalized || false}
            onChange={(e) =>
              onChange({
                ...config,
                stackedBarConfig: {
                  ...stackedBarConfig,
                  normalized: e.target.checked,
                },
              })
            }
            className="w-5 h-5"
          />
          <label htmlFor="normalized" className="font-medium cursor-pointer">
            Mostrar como porcentaje (100% apilado)
          </label>
        </div>
      </div>

      <div className="flex gap-4 mt-6">
        <button
          onClick={onPrev}
          className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300"
        >
          ← Atrás
        </button>
        <button
          onClick={onNext}
          disabled={!isValid}
          className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Vista Previa →
        </button>
      </div>
    </div>
  )
}